import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Paper,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const EditDatasetModal = ({ open, onClose, datasetId, currentEventName, onUpdateSuccess }) => {
  const [eventName, setEventName] = useState(currentEventName || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    setEventName(currentEventName || '');
    setError('');
    setSuccessMessage('');
  }, [currentEventName, open]);

  const handleSubmit = async () => {
    setError('');
    setSuccessMessage('');
    if (!eventName) {
      setError('Event name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/v1/freedata/freedatasets/${datasetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event_name: eventName }),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update dataset');
      }

      const data = await response.json();
      setSuccessMessage(data.message || 'Dataset updated successfully');
      setLoading(false);
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      // Close modal after short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          overflow: 'visible'
        }
      }}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          p: 0,
          borderRadius: '12px',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)'
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out',
            zIndex: 1
          }}
          aria-label="back"
        >
          <ArrowBackIcon />
        </IconButton>

        <Box
          sx={{
            background: 'linear-gradient(45deg, #1976d2, #2196f3)',
            color: 'white',
            py: 3,
            px: 4,
            textAlign: 'center'
          }}
        >
          <DialogTitle
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              p: 0,
              mt: 1
            }}
          >
            Edit Dataset
          </DialogTitle>
        </Box>

        <DialogContent
          sx={{
            py: 4,
            px: 4,
            minHeight: '150px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          <TextField
            label="Event Name"
            variant="outlined"
            fullWidth
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            disabled={loading}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            pb: 3,
            justifyContent: 'center',
            gap: 2
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              px: 4,
              borderRadius: '8px',
              fontWeight: 'bold',
              boxShadow: '0 2px 10px rgba(25, 118, 210, 0.3)'
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={onClose}
            sx={{
              px: 4,
              borderRadius: '8px',
              fontWeight: 'bold'
            }}
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default EditDatasetModal;
